import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Spinner,
  Modal,
  ModalHeader,
  Button,
  ModalBody,
  Table,
  CardHeader,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import {
  getOrders,
  toggleOrderHistoryModal,
  getOrderHistory,
} from "../../store/orders/actions";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getCategoriesSelectList } from "../../store/services/categories/actions";

const OrdersPage = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { showBreadCrumb = true, style = null } = props;

  const {
    // orders
    orders,
    loadingOrders,
    loadingOrderHistory,
    orderHistory,
  } = useSelector((state) => state.Orders);

  document.title = `${t("Orders.Orders")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  const { categoriesSelectList } = useSelector((state) => state.Categories);
  const [selectedOrderId, setSelectedOrderId] = useState();

  const [isOrderHistoryModalOpen, setIsOrderHistoryModalOpen] = useState(false);

  const toggleOrderModal = () => {
    if (!isOrderHistoryModalOpen) {
      dispatch(toggleOrderHistoryModal());
    }
    setIsOrderHistoryModalOpen(!isOrderHistoryModalOpen);
  };

  const onGetOrderHistory = (id) => {
    setSelectedOrderId(id);
    dispatch(getOrderHistory(id));
    toggleOrderModal();
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Orders.Order Code")}</span>
      ),
      selector: (row) => {
        return row.orderCode;
      },
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Common.Service Category")}
        </span>
      ),
      selector: (row) => (
        <div
          className={`align-items-center  ${
            row.deletedAt ? "text-danger" : ""
          }`}
        >
          <span className="flex-grow-1">
            {i18n.language === "ar"
              ? row.service?.arabicName
              : row.service?.name}
          </span>
          <br />
          <span className="flex-grow-1 small">
            {i18n.language === "ar"
              ? row.service?.category?.arabicTitle
              : row.service?.category?.title}
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Roles.Customer")}</span>
      ),
      selector: (row) => (
        <div className="py-1">
          <span className="mb-1">
            {row.user?.firstName + " " + row.user?.lastName}
          </span>
          <br />
          <span className="mt-1 small">
            {row.user?.email ? (
              <a href={`mailto:${row.user?.email}`} title={row.user?.email}>
                {row.user?.email}
              </a>
            ) : (
              " - "
            )}
          </span>
          <br />
          <span className="mt-1 small">
            <a href={`tel:${row.user?.mobile}`} title={row.user?.mobile}>
              {row.user?.mobile}
            </a>
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Orders.Allowed Calls")}
        </span>
      ),
      selector: (row) => {
        return row.limitOfCalls ? row.limitOfCalls : "n/a";
      },
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Orders.Contact Preference")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            <span className="text-success">
              {" "}
              {t("Common.Whatsapp")}
              {": "}
              {row.communicationThrWhatsapp ? "Yes" : "No"}
            </span>
            <br />
            <span className="text-primary">
              {t("Common.Phone Call")}
              {": "}
              {row.communicationThrCall ? "Yes" : "No"}
            </span>
          </div>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Orders.Calls History")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            <a
              style={{ cursor: "pointer" }}
              className="link"
              title={t("Orders.View Order History")}
              onClick={() => {
                onGetOrderHistory(row.id);
              }}
            >
              {t("Orders.View Order History")} ({row.numberOfCallsMade})
            </a>
          </div>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Placed At")}</span>
      ),
      selector: (row) => (
        <span className={`${row.deletedAt ? "text-danger" : ""}`}>
          {" "}
          {new Date(row.createdAt).toLocaleDateString()}{" "}
          {new Date(row.createdAt).toLocaleTimeString()}
        </span>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Deleted At")}</span>
      ),
      selector: (row) => {
        if (row.deletedAt) {
          if (row.deletedAt) {
            return (
              <span className={`${row.deletedAt ? "text-danger" : ""}`}>
                {new Date(row.deletedAt).toLocaleDateString()}{" "}
                {new Date(row.deletedAt).toLocaleTimeString()}
              </span>
            );
          }
        } else {
          return <span>{t("Common.Not Deleted")}</span>;
        }
      },
      sortable: true,
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "createdAt",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "DESC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getOrders({ ...query, customerId: props.customerId }));
    dispatch(getCategoriesSelectList());
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.push({ search: query });
  }, [request]);

  useEffect(() => {
    setRows(orders?.data);
    setTotalRows(orders.meta?.totalItems);
  }, [orders, loadingOrders]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(
      getOrders({ ...request, limit: pageSize, customerId: props.customerId })
    );
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(
      getOrders({ ...request, page: page, customerId: props.customerId })
    );
  };

  const getSortByColumnName = (id) => {
    let sortBy;
    switch (id) {
      case 1:
        sortBy = "orderCode";
        break;
      case 4:
        sortBy = "limitOfCalls";
        break;
      case 7:
        sortBy = "createdAt";
        break;
      case 8:
        sortBy = "deletedAt";
        break;
      default:
        sortBy = "createdAt";
        break;
    }
    return sortBy;
  };

  const getSortByColumnId = (name) => {
    let sortBy;
    switch (name) {
      case "orderCode":
        sortBy = 1;
        break;
      case "limitOfCalls":
        sortBy = 4;
        break;
      case "createdAt":
        sortBy = 7;
        break;
      case "deletedAt":
        sortBy = 8;
        break;
      default:
        sortBy = 7;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getOrders({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
        customerId: props.customerId,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(7);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);

  const onChangeCategoryFilter = (value) => {
    value = value === "all" ? null : value;
    dispatch(
      getOrders({
        ...request,
        page: 1,
        categoryId: value,
        customerId: props.customerId,
      })
    );
  };

  return (
    <React.Fragment>
      <div className="page-content" style={style}>
        <Container fluid>
          {showBreadCrumb && (
            <BreadCrumb
              title={t("Orders.Orders")}
              pageTitle={t("Orders.Orders")}
            />
          )}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm-auto">
                      <div className="">
                        <div className="search-box ms-2">
                          <label>
                            {t("Common.Filter by Service Category")}
                          </label>
                          <select
                            className="form-select mb-3"
                            aria-label="Default select example"
                            onChange={({ target }) =>
                              onChangeCategoryFilter(target.value)
                            }
                          >
                            <option value={"all"} key={null}>
                              {t("Common.All")}
                            </option>
                            {categoriesSelectList &&
                              categoriesSelectList.map(({ id, value }) => (
                                <option value={id} key={id}>
                                  {i18n.language === "ar" ? value.ar : value.en}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingOrders}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Order History Modal */}
      <Modal
        isOpen={isOrderHistoryModalOpen}
        toggle={() => {
          toggleOrderModal();
        }}
        centered
        size="xl"
      >
        <ModalHeader className="bg-light p-3">
          &nbsp;
          {t("Orders.Order History")}
          <Button
            type="button"
            onClick={() => {
              setSelectedOrderId(null);
              setIsOrderHistoryModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <ModalBody>
          <Container fluid>
            <Row>
              <Col lg={12}>
                <Card>
                  <CardHeader>
                    {orderHistory && (
                      <table>
                        <tr>
                          <td width={150}>
                            <h6>
                              <b> {t("Roles.Customer")}</b>:
                            </h6>
                          </td>
                          <td>
                            <h6>{orderHistory.customerName}</h6>
                          </td>
                        </tr>
                        {orderHistory.orderCategory && (
                          <tr>
                            <td width={150}>
                              <h6>
                                <b>{t("Fields.Category")}</b>:
                              </h6>
                            </td>
                            <td>
                              <h6>
                                {i18n.language === "ar"
                                  ? orderHistory.orderCategory.ar
                                  : orderHistory.orderCategory.en}
                              </h6>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td width={150}>
                            <h6>
                              <b>{t("Common.Placed At")}</b>:
                            </h6>
                          </td>
                          <td>
                            <h6>
                              {orderHistory.orderDate &&
                                new Date(
                                  orderHistory.orderDate
                                ).toLocaleDateString() +
                                  " " +
                                  new Date(
                                    orderHistory.orderDate
                                  ).toLocaleTimeString()}
                            </h6>
                          </td>
                        </tr>
                      </table>
                    )}
                  </CardHeader>
                  <CardBody>
                    {loadingOrderHistory ? (
                      <div className="d-flex justify-content-center align-items-center">
                        <Spinner
                          size="md"
                          className="flex-shrink-0 m-5"
                          role="status"
                        ></Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-striped table-nowrap align-middle mb-0">
                          <thead>
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Name</th>
                              <th scope="col" className="text-center">
                                {t("Fields.Mobile")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("Orders.Communication Method")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("Orders.Call Time")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("Orders.Time Difference")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderHistory.history &&
                            orderHistory.history.length > 0 ? (
                              orderHistory.history.map((item) => (
                                <tr>
                                  <td className="fw-medium" width={50}>
                                    {item.seqNo}
                                  </td>
                                  <td width={200}>{item.technicianName}</td>
                                  <td className="text-center" width={120}>
                                    <a
                                      href={`tel:${item.technicianMobile}`}
                                      title={item.technicianMobile}
                                    >
                                      {item.technicianMobile}
                                    </a>
                                    {}
                                  </td>
                                  <td className="text-center" width={200}>
                                    {item.communicationType === "whatsapp"
                                      ? t("Common.Whatsapp")
                                      : item.communicationType === "call"
                                      ? t("Common.Phone Call")
                                      : "n/a"}
                                  </td>
                                  <td className="text-center" width={200}>
                                    {new Date(
                                      item.actionDate
                                    ).toLocaleDateString() +
                                      " " +
                                      new Date(
                                        item.actionDate
                                      ).toLocaleTimeString()}
                                  </td>
                                  <td width={100} className="text-center">
                                    {item.actionDifferenceFromOrder}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center">
                                  {t(
                                    "Pagination.There are no records to display"
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default OrdersPage;
